import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useParties } from '../contexts/PartyContext';
import { useGroups } from '../contexts/GroupContext';
import PartyForm from '../components/parties/PartyForm';
import PartyInvitationManager from '../components/parties/PartyInvitationManager';
import PartyCard from '../components/parties/PartyCard';
import PageContainer from '../components/PageContainer';

export default function PartiesPage() {
  const { parties, loading, error, deleteParty } = useParties();
  const { groups } = useGroups();
  const [formOpen, setFormOpen] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);

  const handleOpenForm = () => {
    setEditingParty(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingParty(null);
  };

  const handleEditParty = (party) => {
    setEditingParty(party);
    setFormOpen(true);
  };

  const handleDeleteParty = async (party) => {
    if (window.confirm(`Are you sure you want to delete "${party.name}"? This action cannot be undone.`)) {
      try {
        await deleteParty(party.id);
      } catch (error) {
        console.error('Error deleting party:', error);
        alert('Failed to delete party. Please try again.');
      }
    }
  };

  const handleInvite = (party) => {
    setSelectedParty(party);
    setInvitationDialogOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            My Parties
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage parties, invite group members, and track RSVPs.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          onClick={handleOpenForm}
          disabled={groups.length === 0}
        >
          Create New Party
        </Button>
      </Box>

      {groups.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You need to create or join a group before you can create a party.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {parties.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'transparent',
            border: '2px dashed rgba(255,255,255,0.1)'
          }}
        >
          <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No parties yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first party to get started with event planning!
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            disabled={groups.length === 0}
          >
            Create Your First Party
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {parties.map((party) => (
            <Grid item xs={12} sm={6} md={4} key={party.id}>
              <PartyCard
                party={party}
                onEdit={handleEditParty}
                onDelete={handleDeleteParty}
                onInvite={handleInvite}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <PartyForm
        open={formOpen}
        onClose={handleCloseForm}
        party={editingParty}
        mode={editingParty ? 'edit' : 'create'}
      />

      <PartyInvitationManager
        open={invitationDialogOpen}
        onClose={() => setInvitationDialogOpen(false)}
        party={selectedParty}
        onInvitationsSent={(count) => {
          console.log(`Sent ${count} invitations successfully`);
        }}
      />
    </PageContainer>
  );
}
